/**
 * Editor state machine for ArticleEditor. Co-locates everything the editor
 * page needs into one `useReducer`:
 *
 *   - Form fields (title, summary, category, visibility, three section bodies)
 *   - The `expected_version` we last saw from the server (drives optimistic
 *     locking on save)
 *   - Dirty flag (any field deviates from the loaded baseline)
 *   - Async state: idle | saving | publishing | deleting
 *   - Conflict state when a PUT comes back 409
 *
 * Loading the article and submitting saves are imperative — the reducer
 * is the source of truth for what the editor will send on next save, but
 * actually firing the request happens in the component (or a thin helper
 * function exported here).
 *
 * Why useReducer not Zustand: this state is scoped to a single mounted
 * editor; nothing else in the app reads it.
 */

import { useReducer, useCallback } from "react";

import {
  ResearchArticleDetail,
  ResearchArticleIndexItem,
  ResearchArticleStatus,
  ResearchArticleVisibility,
  ResearchCategory,
  SearchableSavedQuery,
} from "../../../../models/Research";

export interface EditorFields {
  title: string;
  summary: string;
  categoryId: string | null;
  visibility: ResearchArticleVisibility;
  bodyMd: string;
}

/**
 * One pending embed staged on the new-article form. We carry display
 * fields (`name`, `description`) so the row can render without a
 * second backend call before save. `localId` is a client-side UUID
 * used only for React keys + the reducer's reorder/edit/delete
 * lookups — once the article is created, the server assigns real
 * `research_article_queries.id` values and the editor refetches.
 */
export interface PendingEmbed {
  localId: string;
  saved_query_id: string;
  name: string;
  description: string | null;
  title_override: string | null;
}

/** One pending related-article link staged on the new-article form. */
export interface PendingRelated {
  slug: string;
  title: string;
  category: ResearchCategory | null;
}

/**
 * Template inserted into the body for brand-new articles. Establishes
 * the three canonical research-writeup sections (Executive Summary /
 * Methodology / Findings) as h1s, but the structure is suggestion-only
 * — authors can restructure freely.
 */
export const NEW_ARTICLE_BODY_TEMPLATE = [
  "# Executive Summary",
  "",
  "",
  "",
  "# Methodology",
  "",
  "",
  "",
  "# Findings",
  "",
  "",
].join("\n");

export type EditorAsyncStatus =
  | "idle"
  | "loading"
  | "saving"
  | "publishing"
  | "unpublishing"
  | "deleting";

export interface ConflictState {
  message: string;
  currentVersion: number | null;
}

export interface EditorState {
  /** True when we're editing an existing article (slug in URL).  */
  isExisting: boolean;
  /** Bound article metadata once loaded — title/slug/status from the server. */
  loaded: ResearchArticleDetail | null;
  /** Current form values. */
  fields: EditorFields;
  /** The server-side version we're tracking for optimistic lock. */
  expectedVersion: number;
  /** Form is dirty vs the last loaded/saved baseline. */
  dirty: boolean;
  /** Change note to attach to next save. Cleared after save. */
  changeNote: string;
  /** Async status of the editor. */
  status: EditorAsyncStatus;
  /** Error from the most recent failed action. Cleared on next action. */
  error: string | null;
  /** Populated when a save returns 409. */
  conflict: ConflictState | null;
  /** Embeds staged on the new-article form, sent with the create POST.
   *  Always empty for existing articles — those route through the
   *  API-driven `EmbeddedQueriesList` instead. */
  pendingEmbeds: PendingEmbed[];
  /** Related-article links staged on the new-article form. Same model
   *  as `pendingEmbeds`. */
  pendingRelated: PendingRelated[];
}

const EMPTY_FIELDS: EditorFields = {
  title: "",
  summary: "",
  categoryId: null,
  visibility: "private",
  bodyMd: "",
};

export function newEditorState(isExisting: boolean): EditorState {
  return {
    isExisting,
    loaded: null,
    fields: isExisting
      ? { ...EMPTY_FIELDS }
      : { ...EMPTY_FIELDS, bodyMd: NEW_ARTICLE_BODY_TEMPLATE },
    expectedVersion: 1,
    dirty: false,
    changeNote: "",
    status: isExisting ? "loading" : "idle",
    error: null,
    conflict: null,
    pendingEmbeds: [],
    pendingRelated: [],
  };
}

// Lightweight client-side id for `pendingEmbeds`. Used as React key
// and as the lookup token for reorder / remove / title-edit reducer
// actions. Doesn't need to be cryptographically anything — we throw
// it away once the article is created and the editor refetches.
function newLocalId(): string {
  return `pending-${Math.random().toString(36).slice(2, 11)}`;
}

// ---- Actions ----

type Action =
  | { type: "loaded"; article: ResearchArticleDetail }
  | { type: "loadError"; error: string }
  | { type: "setField"; field: keyof EditorFields; value: string | null }
  | { type: "setChangeNote"; value: string }
  | { type: "startSave" }
  | { type: "saveOk"; article: ResearchArticleDetail }
  | { type: "saveError"; error: string }
  | { type: "saveConflict"; message: string; currentVersion: number | null }
  | { type: "startPublish" }
  | { type: "startUnpublish" }
  | { type: "startDelete" }
  | { type: "clearConflict" }
  // Pending-list edits — only fire on the new-article page.
  | { type: "pendingEmbedAdd"; savedQuery: SearchableSavedQuery }
  | { type: "pendingEmbedRemove"; localId: string }
  | { type: "pendingEmbedMove"; localId: string; direction: -1 | 1 }
  | {
      type: "pendingEmbedSetTitle";
      localId: string;
      titleOverride: string | null;
    }
  | { type: "pendingRelatedAdd"; article: ResearchArticleIndexItem }
  | { type: "pendingRelatedRemove"; slug: string };

function fieldsFromArticle(a: ResearchArticleDetail): EditorFields {
  return {
    title: a.title,
    summary: a.summary ?? "",
    categoryId: a.category?.id ?? null,
    visibility: a.visibility ?? "private",
    bodyMd: a.body_md ?? "",
  };
}

function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case "loaded":
      return {
        ...state,
        loaded: action.article,
        fields: fieldsFromArticle(action.article),
        expectedVersion: action.article.current_version,
        dirty: false,
        changeNote: "",
        status: "idle",
        error: null,
        conflict: null,
      };

    case "loadError":
      return { ...state, status: "idle", error: action.error };

    case "setField":
      // `value` is null only for categoryId.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value as never },
        dirty: true,
        conflict: null,
      };

    case "setChangeNote":
      return { ...state, changeNote: action.value };

    case "startSave":
      return { ...state, status: "saving", error: null, conflict: null };

    case "startPublish":
      return { ...state, status: "publishing", error: null, conflict: null };

    case "startUnpublish":
      return { ...state, status: "unpublishing", error: null, conflict: null };

    case "startDelete":
      return { ...state, status: "deleting", error: null };

    case "saveOk":
      return {
        ...state,
        loaded: action.article,
        fields: fieldsFromArticle(action.article),
        expectedVersion: action.article.current_version,
        dirty: false,
        changeNote: "",
        status: "idle",
        error: null,
        conflict: null,
      };

    case "saveError":
      return { ...state, status: "idle", error: action.error };

    case "saveConflict":
      return {
        ...state,
        status: "idle",
        conflict: {
          message: action.message,
          currentVersion: action.currentVersion,
        },
      };

    case "clearConflict":
      return { ...state, conflict: null };

    case "pendingEmbedAdd": {
      const sq = action.savedQuery;
      const next: PendingEmbed = {
        localId: newLocalId(),
        saved_query_id: sq.id,
        name: sq.name,
        description: sq.description,
        title_override: null,
      };
      return { ...state, pendingEmbeds: [...state.pendingEmbeds, next] };
    }

    case "pendingEmbedRemove":
      return {
        ...state,
        pendingEmbeds: state.pendingEmbeds.filter(
          (e) => e.localId !== action.localId,
        ),
      };

    case "pendingEmbedMove": {
      const idx = state.pendingEmbeds.findIndex(
        (e) => e.localId === action.localId,
      );
      const target = idx + action.direction;
      if (
        idx < 0 ||
        target < 0 ||
        target >= state.pendingEmbeds.length
      ) {
        return state;
      }
      const next = state.pendingEmbeds.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...state, pendingEmbeds: next };
    }

    case "pendingEmbedSetTitle":
      return {
        ...state,
        pendingEmbeds: state.pendingEmbeds.map((e) =>
          e.localId === action.localId
            ? { ...e, title_override: action.titleOverride }
            : e,
        ),
      };

    case "pendingRelatedAdd": {
      const a = action.article;
      // No-op if this slug is already in the pending set.
      if (state.pendingRelated.some((r) => r.slug === a.slug)) return state;
      const next: PendingRelated = {
        slug: a.slug,
        title: a.title,
        category: a.category,
      };
      return { ...state, pendingRelated: [...state.pendingRelated, next] };
    }

    case "pendingRelatedRemove":
      return {
        ...state,
        pendingRelated: state.pendingRelated.filter(
          (r) => r.slug !== action.slug,
        ),
      };

    default:
      return state;
  }
}

// ---- Hook ----

export interface UseArticleEditor {
  state: EditorState;
  setField: (field: keyof EditorFields, value: string | null) => void;
  setChangeNote: (value: string) => void;
  // Action dispatchers — components fire these around their async work.
  onLoaded: (article: ResearchArticleDetail) => void;
  onLoadError: (msg: string) => void;
  beginSave: () => void;
  beginPublish: () => void;
  beginUnpublish: () => void;
  beginDelete: () => void;
  onSaveOk: (article: ResearchArticleDetail) => void;
  onSaveError: (msg: string) => void;
  onSaveConflict: (msg: string, currentVersion: number | null) => void;
  clearConflict: () => void;
  // Pending-list editors — used by the new-article page only.
  pendingEmbedAdd: (savedQuery: SearchableSavedQuery) => void;
  pendingEmbedRemove: (localId: string) => void;
  pendingEmbedMove: (localId: string, direction: -1 | 1) => void;
  pendingEmbedSetTitle: (
    localId: string,
    titleOverride: string | null,
  ) => void;
  pendingRelatedAdd: (article: ResearchArticleIndexItem) => void;
  pendingRelatedRemove: (slug: string) => void;
}

export function useArticleEditor(isExisting: boolean): UseArticleEditor {
  const [state, dispatch] = useReducer(reducer, isExisting, newEditorState);

  const setField = useCallback(
    (field: keyof EditorFields, value: string | null) =>
      dispatch({ type: "setField", field, value }),
    [],
  );
  const setChangeNote = useCallback(
    (value: string) => dispatch({ type: "setChangeNote", value }),
    [],
  );
  const onLoaded = useCallback(
    (article: ResearchArticleDetail) =>
      dispatch({ type: "loaded", article }),
    [],
  );
  const onLoadError = useCallback(
    (error: string) => dispatch({ type: "loadError", error }),
    [],
  );
  const beginSave = useCallback(() => dispatch({ type: "startSave" }), []);
  const beginPublish = useCallback(
    () => dispatch({ type: "startPublish" }),
    [],
  );
  const beginUnpublish = useCallback(
    () => dispatch({ type: "startUnpublish" }),
    [],
  );
  const beginDelete = useCallback(() => dispatch({ type: "startDelete" }), []);
  const onSaveOk = useCallback(
    (article: ResearchArticleDetail) =>
      dispatch({ type: "saveOk", article }),
    [],
  );
  const onSaveError = useCallback(
    (error: string) => dispatch({ type: "saveError", error }),
    [],
  );
  const onSaveConflict = useCallback(
    (message: string, currentVersion: number | null) =>
      dispatch({ type: "saveConflict", message, currentVersion }),
    [],
  );
  const clearConflict = useCallback(
    () => dispatch({ type: "clearConflict" }),
    [],
  );
  const pendingEmbedAdd = useCallback(
    (savedQuery: SearchableSavedQuery) =>
      dispatch({ type: "pendingEmbedAdd", savedQuery }),
    [],
  );
  const pendingEmbedRemove = useCallback(
    (localId: string) => dispatch({ type: "pendingEmbedRemove", localId }),
    [],
  );
  const pendingEmbedMove = useCallback(
    (localId: string, direction: -1 | 1) =>
      dispatch({ type: "pendingEmbedMove", localId, direction }),
    [],
  );
  const pendingEmbedSetTitle = useCallback(
    (localId: string, titleOverride: string | null) =>
      dispatch({ type: "pendingEmbedSetTitle", localId, titleOverride }),
    [],
  );
  const pendingRelatedAdd = useCallback(
    (article: ResearchArticleIndexItem) =>
      dispatch({ type: "pendingRelatedAdd", article }),
    [],
  );
  const pendingRelatedRemove = useCallback(
    (slug: string) => dispatch({ type: "pendingRelatedRemove", slug }),
    [],
  );

  return {
    state,
    setField,
    setChangeNote,
    onLoaded,
    onLoadError,
    beginSave,
    beginPublish,
    beginUnpublish,
    beginDelete,
    onSaveOk,
    onSaveError,
    onSaveConflict,
    clearConflict,
    pendingEmbedAdd,
    pendingEmbedRemove,
    pendingEmbedMove,
    pendingEmbedSetTitle,
    pendingRelatedAdd,
    pendingRelatedRemove,
  };
}

// Helpers exported for the page to use when composing requests.

export function statusFor(state: EditorState): ResearchArticleStatus {
  return state.loaded?.status ?? "draft";
}

export function categoryById(
  categories: ResearchCategory[],
  id: string | null,
): ResearchCategory | null {
  if (!id) return null;
  return categories.find((c) => c.id === id) ?? null;
}
